// ui/src/components/tenant-admin/sections/VariableCompAllocation.jsx

/**
 * @file VariableCompAllocation.jsx
 * @description Plan document section for allocating 100% of variable
 *   compensation across the primary earnings spine and enabled secondary
 *   measures. Renders an editable weight table in draft mode and a read-only
 *   bar chart summary in approved/locked state. No API calls — prop-driven.
 *   Weight state is persisted by the parent plan document save flow.
 * @module tenant-admin/sections
 * @author data-fit LLC · DecisionFlow · S775
 * @see api_spec_fileA_v4_final.md §1.7 — POST /api/v1/plans (ep 7)
 * @see api_spec_fileA_v4_final.md §1.12 — amendments/begin (ep 12)
 */

import PropTypes from 'prop-types';
import { useState } from 'react';

// Canonical spine label map — mirrors usePlanDocument.js SPINE_LABELS.
// weights.primarySpineLabel may be a raw key (e.g. 'revenue_bookings') when
// the layer1_config JSONB was written before the normalizer label fallback was
// introduced. Always resolve through this map before display.
const SPINE_LABELS = {
  revenue_bookings:       'Revenue / Bookings',
  transaction_commission: 'Transaction Commission',
  volume_quantity:        'Volume / Quantity',
  market_share:           'Market Share / Penetration',
  grr_nrr_retention:      'Retention (GRR / NRR)',
  mbo_objective:          'MBO / Objective',
  margin_profit:          'Margin / Profit',
  bonus_pool:             'Bonus Pool / Allocation',
  pharma_script:          'Pharma / Script Volume',
  // Legacy keys from old arch — display fallback only
  ARR:         'Revenue / Bookings',
  arr:         'Revenue / Bookings',
  revenue:     'Revenue / Bookings',
  mbo:         'MBO / Objective',
  nps:         'MBO / Objective',
  units:       'Volume / Quantity',
  transaction: 'Transaction Commission',
};

/**
 * Resolve a human-readable spine label from either a raw key or an already-
 * resolved display string. If the value is a raw key in SPINE_LABELS, return
 * the display string. Otherwise return the value as-is (already human-readable).
 */
function resolveSpineLabel(label) {
  if (!label) return 'Primary earnings model';
  return SPINE_LABELS[label] ?? label;
}

const clampWeight = (value) => {
  if (!Number.isFinite(value)) {
    return 0;
  }

  return Math.min(100, Math.max(0, Math.round(value * 10) / 10));
};

const formatPercent = (value) => {
  const normalized = Math.round(value * 10) / 10;
  return Number.isInteger(normalized)
    ? `${normalized}%`
    : `${normalized.toFixed(1)}%`;
};

/**
 * WeightInput — local draft string state so the user can type freely
 * (e.g. clear "100" and type "80") without clampWeight fighting each keystroke.
 * Commits the clamped numeric value to the parent only on blur or Enter.
 */
function WeightInput({ value, ariaLabel, disabled, onCommit }) {
  const [draft, setDraft] = useState(String(value));

  // Sync draft when parent value changes externally (e.g. spine change resets to 100)
  // Only sync when not actively editing — we detect this by comparing parsed values
  const parsedDraft = draft === '' ? 0 : Number(draft);
  const externalChange = Number.isFinite(parsedDraft) && Math.abs(parsedDraft - value) > 0.05
    && document.activeElement?.getAttribute('aria-label') !== ariaLabel;

  if (externalChange) {
    // Can't call setState during render — use a ref pattern via key instead
    // This is handled by key={value} on the input below
  }

  function handleChange(e) {
    setDraft(e.target.value);
  }

  function handleBlur() {
    const num = draft === '' ? 0 : Number(draft);
    const clamped = clampWeight(Number.isFinite(num) ? num : 0);
    setDraft(String(clamped));
    onCommit(clamped);
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter') {
      e.currentTarget.blur();
    }
  }

  return (
    <div className="variable-allocation-percent-input">
      <input
        key={externalChange ? value : undefined}
        className="variable-allocation-input"
        type="text"
        inputMode="decimal"
        aria-label={ariaLabel}
        value={externalChange ? String(value) : draft}
        disabled={disabled}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
      />
      <span className="variable-allocation-percent-symbol" aria-hidden="true">
        %
      </span>
    </div>
  );
}

/**
 * @typedef {Object} SecondaryMeasureWeight
 * @property {string} key
 * @property {string} label
 * @property {number} weight
 * @property {boolean} enabled
 */

/**
 * @typedef {Object} WeightsObject
 * @property {string} primarySpineLabel
 * @property {number} primarySpineWeight
 * @property {SecondaryMeasureWeight[]} secondaryMeasures
 */

/**
 * @param {Object} props
 * @param {WeightsObject} props.weights
 * @param {boolean} props.readOnly
 * @param {(weights: WeightsObject) => void} props.onChange
 * @param {string|null} props.validationError
 * @returns {JSX.Element}
 */
export default function VariableCompAllocation({
  weights,
  readOnly,
  onChange,
  validationError,
}) {
  const enabledSecondaryTotal = weights.secondaryMeasures.reduce(
    (sum, measure) => sum + (measure.enabled ? measure.weight : 0),
    0,
  );

  const total = Math.round(
    (weights.primarySpineWeight + enabledSecondaryTotal) * 10,
  ) / 10;

  const remaining = Math.round((100 - total) * 10) / 10;

  const updatePrimary = (clampedValue) => {
    onChange({
      ...weights,
      primarySpineWeight: clampedValue,
    });
  };

  const updateSecondaryWeight = (key, clampedValue) => {
    onChange({
      ...weights,
      secondaryMeasures: weights.secondaryMeasures.map((measure) =>
        measure.key === key
          ? {
              ...measure,
              weight: clampedValue,
              enabled: clampedValue > 0 ? true : measure.enabled,
            }
          : measure,
      ),
    });
  };

  const toggleSecondary = (key, enabled) => {
    onChange({
      ...weights,
      secondaryMeasures: weights.secondaryMeasures.map((measure) =>
        measure.key === key
          ? {
              ...measure,
              enabled,
              weight: enabled
                ? measure.weight > 0
                  ? measure.weight
                  : 0
                : 0,
            }
          : measure,
      ),
    });
  };

  return (
    <section
      className="modifier-manager-card variable-allocation"
      aria-labelledby="variable-allocation-heading"
    >
      <style>{`
        .variable-allocation {
          background: rgb(var(--color-surface));
          border: 1px solid rgb(var(--color-border));
          color: rgb(var(--color-text));
          font-family: "IBM Plex Sans", sans-serif;
        }

        .variable-allocation .modifier-manager-header {
          border-bottom: 1px solid rgb(var(--color-border));
          padding: 16px;
        }

        .variable-allocation .modifier-manager-body {
          display: flex;
          flex-direction: column;
          gap: 16px;
          padding: 16px;
        }

        .variable-allocation-heading {
          font-size: 18px;
          font-weight: 600;
          margin: 0;
        }

        .variable-allocation-subtitle {
          color: rgb(var(--color-text-muted));
          font-size: 13px;
          line-height: 1.5;
          margin: 4px 0 0;
        }

        .variable-allocation-table {
          border-collapse: collapse;
          min-width: 620px;
          width: 100%;
        }

        .variable-allocation-table th,
        .variable-allocation-table td {
          border-bottom: 1px solid rgb(var(--color-border));
          padding: 10px 8px;
          text-align: left;
          vertical-align: middle;
        }

        .variable-allocation-table th {
          color: rgb(var(--color-text-muted));
          font-size: 12px;
          font-weight: 600;
        }

        .variable-allocation-measure {
          font-size: 14px;
          font-weight: 500;
        }

        .variable-allocation-type {
          color: rgb(var(--color-text-muted));
          display: block;
          font-size: 12px;
          margin-top: 2px;
        }

        .variable-allocation-input {
          background: rgb(var(--color-surface));
          border: 1px solid rgb(var(--color-border));
          border-radius: 6px;
          color: rgb(var(--color-text));
          font: inherit;
          max-width: 110px;
          min-height: 38px;
          padding: 7px 9px;
          width: 100%;
        }

        .variable-allocation-input:disabled {
          background: rgb(var(--color-surface-muted));
          color: rgb(var(--color-text-muted));
        }

        .variable-allocation-percent-input {
          align-items: center;
          display: flex;
          gap: 6px;
        }

        .variable-allocation-percent-symbol {
          color: rgb(var(--color-text-muted));
        }

        .variable-allocation-counter {
          align-items: center;
          border: 1px solid rgb(var(--color-border));
          border-radius: 8px;
          display: flex;
          gap: 8px;
          justify-content: space-between;
          padding: 12px;
        }

        .variable-allocation-counter-label {
          font-size: 13px;
          font-weight: 600;
        }

        .variable-allocation-counter-value {
          font-family: "IBM Plex Mono", monospace;
          font-size: 14px;
          font-weight: 600;
        }

        .variable-allocation-counter[data-state="balanced"]
          .variable-allocation-counter-value {
          color: rgb(var(--color-success));
        }

        .variable-allocation-counter[data-state="remaining"]
          .variable-allocation-counter-value,
        .variable-allocation-counter[data-state="over"]
          .variable-allocation-counter-value {
          color: rgb(var(--color-warning));
        }

        .variable-allocation .modifier-manager-inline-error {
          background: rgb(var(--color-danger) / 0.06);
          border: 1px solid rgb(var(--color-border));
          border-radius: 8px;
          color: rgb(var(--color-text));
          font-size: 13px;
          line-height: 1.5;
          margin: 0;
          padding: 10px 12px;
        }

        .variable-allocation .modifier-manager-readonly-note {
          background: rgb(var(--color-surface-muted));
          border: 1px solid rgb(var(--color-border));
          border-radius: 8px;
          color: rgb(var(--color-text-muted));
          font-size: 13px;
          line-height: 1.5;
          margin: 0;
          padding: 10px 12px;
        }

        .variable-allocation-bar {
          display: flex;
          height: 12px;
          overflow: hidden;
          width: 100%;
        }

        .variable-allocation-bar-segment {
          background: rgb(var(--color-primary));
          border-right: 1px solid rgb(var(--color-surface)));
          min-width: 0;
        }

        .variable-allocation-bar-segment:last-child {
          border-right: 0;
        }

        .variable-allocation-total {
          color: rgb(var(--color-text-muted));
          font-size: 12px;
          margin: 0;
        }
      `}</style>

      <header className="modifier-manager-header">
        <h2
          id="variable-allocation-heading"
          className="variable-allocation-heading"
        >
          Variable compensation allocation
        </h2>
        <p className="variable-allocation-subtitle">
          Allocate 100% of variable compensation across the primary earnings
          model and enabled secondary measures.
        </p>
      </header>

      <div className="modifier-manager-body">
        {readOnly && (
          <p className="modifier-manager-readonly-note" role="note">
            Variable compensation allocation is read-only for this plan
            version. Changes require a successor amendment.
          </p>
        )}

        <div className="table-scroll">
          <table className="variable-allocation-table">
            <caption className="sr-only">
              Variable compensation component weights
            </caption>
            <thead>
              <tr>
                <th scope="col">Component</th>
                {!readOnly && <th scope="col">Enabled</th>}
                <th scope="col">Weight</th>
              </tr>
            </thead>

            <tbody>
              <tr>
                <th scope="row">
                  <span className="variable-allocation-measure">
                    {resolveSpineLabel(weights.primarySpineLabel)}
                  </span>
                  <span className="variable-allocation-type">
                    Primary earnings model
                  </span>
                </th>

                {!readOnly && <td>Always</td>}

                <td>
                  {readOnly ? (
                    formatPercent(weights.primarySpineWeight)
                  ) : (
                    <WeightInput
                      value={weights.primarySpineWeight}
                      ariaLabel={`${resolveSpineLabel(weights.primarySpineLabel)} weight`}
                      disabled={false}
                      onCommit={(clamped) => updatePrimary(clamped)}
                    />
                  )}
                </td>
              </tr>

              {weights.secondaryMeasures.map((measure) => (
                <tr key={measure.key}>
                  <th scope="row">
                    <span className="variable-allocation-measure">
                      {measure.label}
                    </span>
                    <span className="variable-allocation-type">
                      Secondary measure
                    </span>
                  </th>

                  {!readOnly && (
                    <td>
                      <label>
                        <input
                          type="checkbox"
                          checked={measure.enabled}
                          onChange={(event) =>
                            toggleSecondary(
                              measure.key,
                              event.target.checked,
                            )
                          }
                        />
                        <span className="sr-only">
                          Enable {measure.label}
                        </span>
                      </label>
                    </td>
                  )}

                  <td>
                    {readOnly ? (
                      measure.enabled
                        ? formatPercent(measure.weight)
                        : 'Disabled'
                    ) : (
                      <WeightInput
                        value={measure.weight}
                        ariaLabel={`${measure.label} weight`}
                        disabled={!measure.enabled}
                        onCommit={(clamped) =>
                          updateSecondaryWeight(measure.key, clamped)
                        }
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {readOnly && total > 0 && (
          <div
            className="variable-allocation-bar"
            role="img"
            aria-label={`Variable compensation allocation totals ${formatPercent(
              total,
            )}`}
          >
            <div
              className="variable-allocation-bar-segment"
              style={{ width: `${weights.primarySpineWeight}%` }}
            />
            {weights.secondaryMeasures
              .filter((measure) => measure.enabled && measure.weight > 0)
              .map((measure) => (
                <div
                  key={measure.key}
                  className="variable-allocation-bar-segment"
                  style={{ width: `${measure.weight}%` }}
                />
              ))}
          </div>
        )}

        <div
          className="variable-allocation-counter"
          data-state={
            remaining === 0
              ? 'balanced'
              : remaining > 0
                ? 'remaining'
                : 'over'
          }
          aria-live="polite"
        >
          <span className="variable-allocation-counter-label">
            Remaining
          </span>
          <span className="variable-allocation-counter-value">
            {formatPercent(remaining)}
          </span>
        </div>

        <p className="variable-allocation-total">
          Current total: {formatPercent(total)}
        </p>

        {validationError && (
          <p
            className="modifier-manager-inline-error"
            role="alert"
          >
            {validationError}
          </p>
        )}
      </div>
    </section>
  );
}

VariableCompAllocation.propTypes = {
  weights: PropTypes.shape({
    primarySpineLabel: PropTypes.string.isRequired,
    primarySpineWeight: PropTypes.number.isRequired,
    secondaryMeasures: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        weight: PropTypes.number.isRequired,
        enabled: PropTypes.bool.isRequired,
      }),
    ).isRequired,
  }).isRequired,
  readOnly: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  validationError: PropTypes.string,
};
